import React, { useState, useEffect } from 'react';
import { VscAdd, VscCommentDiscussion, VscPerson, VscOrganization, VscSettingsGear, VscClose } from 'react-icons/vsc';
import { getDm, getChatUsers, createGroup, updateGroup, leaveGroup } from '../../api/chat';
import Avatar from '../common/Avatar';
import CreateGroupModal from './CreateGroupModal';
import GroupEditModal from './GroupEditModal';
import './Chat.css';

const TYPE_ORDER = { all: 0, project: 1, engineers: 2, foremen: 3, custom: 4, dm: 5 };

const GROUP_ICONS = {
  all:       <VscOrganization size={14} className="chat-type-icon" />,
  project:   <VscCommentDiscussion size={14} className="chat-type-icon" />,
  engineers: <VscCommentDiscussion size={14} className="chat-type-icon" />,
  foremen:   <VscCommentDiscussion size={14} className="chat-type-icon" />,
  custom:    <VscCommentDiscussion size={14} className="chat-type-icon" />,
  dm:        <VscPerson size={14} className="chat-type-icon" />,
};

function groupDisplayName(group, allUsers, currentUserId) {
  if (group.type === 'dm') {
    const otherId = (group.members || []).find(id => id !== currentUserId);
    const other   = allUsers.find(u => u._id === otherId);
    return other ? (other.displayName || other.username) : 'Direct Message';
  }
  if (group.type === 'project') return group.project?.name ?? group.name;
  return group.name;
}

function ChatSidebar({ socket, currentUser, groups, setGroups, activeGroupId, onOpenChat, onlineUsers = [] }) {
  const [allUsers, setAllUsers]       = useState([]);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [showDmPicker, setShowDmPicker] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  useEffect(() => {
    getChatUsers().then(r => setAllUsers(r.data)).catch(() => {});
  }, []);

  const handleOpenGroup = (group) => {
    setGroups(prev => prev.map(g => g._id === group._id ? { ...g, unread: 0 } : g));
    const name = groupDisplayName(group, allUsers, currentUser?.id);
    onOpenChat(group, name);
  };

  const handleOpenDm = async (user) => {
    try {
      const res = await getDm(user._id);
      const dm  = res.data;
      setGroups(prev => prev.find(g => g._id === dm._id) ? prev : [...prev, dm]);
      handleOpenGroup(dm);
    } catch {}
    setShowDmPicker(false);
  };

  const handleCreateGroup = async (data) => {
    const res = await createGroup(data);
    const group = res.data;
    setGroups(prev => prev.find(g => g._id === group._id) ? prev : [...prev, group]);
    handleOpenGroup(group);
  };

  const handleSaveEdit = async (data) => {
    await updateGroup(editingGroup._id, data);
    setGroups(prev => prev.map(g => g._id === editingGroup._id
      ? { ...g, members: data.members }
      : g
    ));
  };

  const handleLeave = async (group) => {
    await leaveGroup(group._id);
    setGroups(prev => prev.filter(g => g._id !== group._id));
  };

  const regularGroups = [...groups]
    .filter(g => g.type !== 'dm')
    .sort((a, b) => (TYPE_ORDER[a.type] ?? 9) - (TYPE_ORDER[b.type] ?? 9));

  const dms = groups.filter(g => g.type === 'dm');

  return (
    <div className="chat-sidebar">
      {/* Groups */}
      <div className="chat-sidebar-section">
        <div className="chat-sidebar-group-header">
          <span>Groups</span>
          <button className="btn-icon" title="New Group" onClick={() => setShowNewGroup(true)}>
            <VscAdd size={14} />
          </button>
        </div>

        {regularGroups.map(g => (
          <div key={g._id} className="chat-sidebar-row">
            <button
              className={`chat-sidebar-item${g._id === activeGroupId ? ' active' : ''}${g.unread > 0 ? ' unread' : ''}`}
              onClick={() => handleOpenGroup(g)}
            >
              {GROUP_ICONS[g.type]}
              <span className="chat-sidebar-name">
                {groupDisplayName(g, allUsers, currentUser?.id)}
              </span>
              {g.unread > 0 && (
                <span className="chat-unread-badge">{g.unread > 99 ? '99+' : g.unread}</span>
              )}
            </button>
            {g.type === 'custom' && (
              <button
                className="chat-sidebar-action"
                title="Edit group"
                onClick={e => { e.stopPropagation(); setEditingGroup(g); }}
              >
                <VscSettingsGear size={13} />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Direct Messages */}
      <div className="chat-sidebar-section">
        <div className="chat-sidebar-group-header">
          <span>Direct Messages</span>
          <button className="btn-icon" title="New DM" onClick={() => setShowDmPicker(v => !v)}>
            <VscAdd size={14} />
          </button>
        </div>

        {showDmPicker && (
          <div style={{ padding: '0 6px 6px' }}>
            {allUsers
              .filter(u => u._id !== currentUser?.id)
              .map(u => (
                <button
                  key={u._id}
                  className="chat-sidebar-item"
                  style={{ paddingLeft: 10 }}
                  onClick={() => handleOpenDm(u)}
                >
                  <VscPerson size={13} className="chat-type-icon" />
                  <span className={`chat-sender role-${u.role}`}>{u.username}</span>
                </button>
              ))}
          </div>
        )}

        {dms.map(g => {
          const otherId   = (g.members || []).find(id => id !== currentUser?.id);
          const otherUser = allUsers.find(u => u._id === otherId);
          const isOnline  = otherId && onlineUsers.includes(otherId);
          return (
          <div key={g._id} className="chat-sidebar-row">
            <button
              className={`chat-sidebar-item${g._id === activeGroupId ? ' active' : ''}${g.unread > 0 ? ' unread' : ''}`}
              onClick={() => handleOpenGroup(g)}
            >
              <span style={{ position: 'relative', flexShrink: 0 }}>
                <Avatar user={otherUser} size={20} />
                <span className={`online-dot${isOnline ? ' online' : ''}`}
                  style={{ position: 'absolute', bottom: -1, right: -1, width: 7, height: 7 }} />
              </span>
              <span className="chat-sidebar-name">
                {groupDisplayName(g, allUsers, currentUser?.id)}
              </span>
              {g.unread > 0 && (
                <span className="chat-unread-badge">{g.unread > 99 ? '99+' : g.unread}</span>
              )}
            </button>
            <button
              className="chat-sidebar-action"
              title="Leave DM"
              onClick={async e => { e.stopPropagation(); await handleLeave(g); }}
            >
              <VscClose size={13} />
            </button>
          </div>
          );
        })}
      </div>

      {showNewGroup && (
        <CreateGroupModal
          allUsers={allUsers}
          currentUserId={currentUser?.id}
          onClose={() => setShowNewGroup(false)}
          onCreate={handleCreateGroup}
        />
      )}

      {editingGroup && (
        <GroupEditModal
          group={editingGroup}
          allUsers={allUsers}
          currentUserId={currentUser?.id}
          onClose={() => setEditingGroup(null)}
          onSave={handleSaveEdit}
          onLeave={() => handleLeave(editingGroup)}
        />
      )}
    </div>
  );
}

export default ChatSidebar;
